import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { sendMagicLinkCode, validateMagicLink } from '../app/api';
import { setToken } from '../app/auth';
import { useTranslation } from '../app/LanguageContext';
import ThemeModeToggle from '../components/ThemeModeToggle';
import { SavingOverlay } from '../components/SavingOverlay';

const LoginPage = () => {
  const { translate } = useTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [pendingEmail, setPendingEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step !== 'code' || loading) return;
    const id = window.requestAnimationFrame(() => {
      codeInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [step, loading]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setLoading(true);
    try {
      await sendMagicLinkCode(email);
      setPendingEmail(email.trim().toLowerCase());
      setStep('code');
    } catch (err) {
      setError(err.message || translate('auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setLoading(true);
    try {
      const response = await validateMagicLink(pendingEmail, code.trim());
      if (response?.data?.token) {
        setToken(response.data.token);
        navigate('/', { replace: true });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      setError(err.message || translate('auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromCode = () => {
    setStep('email');
    setCode('');
    setError('');
  };

  const codeHint = translate('auth.magic_link_code_hint').replace('{email}', pendingEmail);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ position: 'fixed', top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 16 }, zIndex: 10 }}>
        <ThemeModeToggle />
      </Box>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {step === 'code' ? translate('auth.magic_link_verify_title') : translate('auth.magic_link_title')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {step === 'code' ? (
            <Box component="form" onSubmit={handleValidateCode} sx={{ mt: 1 }} noValidate>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                {codeHint}
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label={translate('auth.magic_link_code_label')}
                name="code"
                autoComplete="one-time-code"
                inputRef={codeInputRef}
                autoFocus
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 64))
                }
                helperText={translate('auth.magic_link_code_helper')}
                inputProps={{ maxLength: 64, 'data-testid': 'input-magic-code' }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 1 }}
                disabled={loading || code.trim().length < 6}
                data-testid="btn-magic-validate"
              >
                {loading ? <CircularProgress size={24} /> : translate('auth.magic_link_validate')}
              </Button>
              <Button fullWidth onClick={handleBackFromCode}>
                {translate('auth.magic_link_back')}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSendCode} sx={{ mt: 1 }} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={translate('auth.email')}
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ 'data-testid': 'input-email' }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
                data-testid="btn-send-code"
              >
                {loading ? translate('common.saving') : translate('auth.magic_link_send_code')}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      <SavingOverlay open={loading} />
    </Container>
    </Box>
  );
};

export default LoginPage;
